/**
 * 前端版本哨兵 —— 解决 web-view 里「服务器已更新，页面还是旧的」。
 *
 * 背景：
 *   构建产物里 js/css/图片都带内容哈希，可以放心长缓存；只有 index.html 是"版本指针"。
 *   但微信 web-view（X5/WKWebView）会把 index.html 也缓存住，而且它的 HTTP 缓存
 *   独立于小程序代码包 —— 在开发者工具里点「清除全部缓存」也清不掉，
 *   结果就是：服务器已经是新版，用户看到的还是上一版，必须手动清缓存才行。
 *
 * 服务端响应头是第一道防线（nginx 给 index.html 加 no-cache，见部署文档步骤 10）。
 * 本模块是第二道防线：万一把构建产物复用了旧文件名、或者中间层（CDN / 代理）
 * 强缓存了 HTML，也能自动把用户顶到新版本上，不需要人工清缓存。
 *
 * 实现要点：
 *   - 只在「小程序端入口」启用，不影响浏览器调试入口。
 *   - 请求必须带 'no-store'，否则 fetch 自己也会命中 HTTP 缓存，问不到真话。
 *   - 防重复刷新用「URL 一次性标记 + sessionStorage 备用标记」双保险，
 *     且**不能用 localStorage** —— 原因见下。
 *
 * 为什么防重复标记不能用 localStorage：
 *   1. **语义不对**：这个标记要表达的是"本次会话已经重试过一次"，
 *      需要的生命周期是「重开即重置」。放 localStorage 会变成"这台设备永远只重试一次"，
 *      跨会话累积，用户下次进来遇到新版也不会被拉起来。
 *   2. **曾被清空过**：本项目 main.js 有一段"版本变化 → 清缓存"的历史逻辑，
 *      原先直接 `localStorage.clear()` 会把标记一起抹掉，导致"标记失效 → 再跳转 →
 *      再被抹掉"的**无限刷新**（实测踩过，7~8 次循环）。该逻辑现已改为只清缓存类 key、
 *      放行登录态，但结论不变 —— 这类控制标记本就不该用持久化存储。
 *   → 改用 URL 标记 + sessionStorage；sessionStorage 与导航生命周期绑定，重开即重置。
 *
 * 另注：sessionStorage 备用标记是必要的，因为 vue-router 的 `next('/login')`
 * 这类跳转只改 path、**不保留原 query**，会把 URL 上的标记弄丢。
 */

import { isMiniProgram } from './env';

/** 构建时确定的本版版本号（由 vue.config.js 注入） */
const HTML_VERSION = process.env.VUE_APP_HTML_VERSION || '';

/** 一次性标记的 query 参数名。跳转后 URL 会带上它，用来短路，避免死循环 */
const RETRY_PARAM = '__xdj_reloaded';

/**
 * 备用标记（sessionStorage）。为什么需要它：
 *   URL 上的 `__xdj_reloaded` 会被 vue-router 的 `next('/login')` 之类跳转丢掉
 *   （改变 path 时不保留原 query），那样就失去了"已重试过"的证据。
 * 为什么用 sessionStorage 而不是 localStorage：
 *   - 生命周期跟标签页/导航绑定，重开小程序自然重置，不会永久卡住；
 *   - 不会被 main.js 那段"清缓存"逻辑波及（该逻辑现已放行登录态，但仍只保证不清 user/store）。
 */
const RETRY_KEY = '__xdj_reloaded_at__';

/** 起刷新检查前先等首屏渲染完成，避免干扰用户第一眼看到的画面 */
const DELAY_MS = 1500;

/** URL 或 sessionStorage 里是否已经留下"重试过"的痕迹 */
function alreadyRetried() {
  try {
    if (new URLSearchParams(window.location.search).get(RETRY_PARAM) === '1') return true;
  } catch (e) {
    /* ignore */
  }
  try {
    return sessionStorage.getItem(RETRY_KEY) === HTML_VERSION;
  } catch (e) {
    return false;
  }
}

/** 记下"已经从版本 X 重试过"，供刷新后判定 */
function markRetried() {
  try {
    sessionStorage.setItem(RETRY_KEY, HTML_VERSION);
  } catch (e) {
    /* ignore */
  }
}

/**
 * 拉一次 index.html（必须绕过缓存），从里面提取构建时注入的版本号。
 * 用正则而不是 DOMParser：这段代码要在首屏尽早跑，尽量不引入额外解析开销。
 */
async function fetchOnlineVersion() {
  const res = await fetch(`/index.html?_t=${Date.now()}`, {
    cache: 'no-store',
    credentials: 'omit',
  });
  if (!res.ok) return null;
  const html = await res.text();
  const m = html.match(/name="xdj-html-version"\s+content="([^"]+)"/);
  return m ? m[1] : null;
}

/**
 * 在 URL 上追加一次性标记后跳转（用 replace 避免污染历史记录）。
 * 不用 location.reload()：reload 会保留原 URL，无法携带"已重试"的标记。
 * 同时写 sessionStorage 备用标记 —— 因为 vue-router 跳转可能丢掉 URL 上的 query。
 */
function reloadWithRetryMark() {
  markRetried();
  const url = new URL(window.location.href);
  url.searchParams.set(RETRY_PARAM, '1');
  window.location.replace(url.toString());
}

/** 抹掉重试标记（已确认是最新版本时调用，让下次发版能正常触发） */
function cleanRetryMark() {
  try {
    sessionStorage.removeItem(RETRY_KEY);
  } catch (e) {
    /* ignore */
  }
  if (!window.location.search.includes(RETRY_PARAM)) return;
  const url = new URL(window.location.href);
  url.searchParams.delete(RETRY_PARAM);
  window.history.replaceState(null, '', url.toString());
}

/**
 * 启动版本哨兵。仅在 web-view 内在跑，失败静默降级，绝不影响正常使用。
 */
export function initVersionCheck() {
  if (!HTML_VERSION) return;
  // 自动化脚本（E2E 截图 / 冒烟）可注入该标记跳过刷新，否则跳转会打断脚本流程
  if (window.__XDJ_DISABLE_VERSION_CHECK__) return;
  if (!isMiniProgram()) return;

  setTimeout(async () => {
    try {
      const online = await fetchOnlineVersion();
      if (!online) return;

      if (online === HTML_VERSION) {
        // 已是最新。若 URL 上残留着上轮的重试标记，用 replace 抹掉，
        // 避免它一直留在地址栏里干扰后续判断。
        if (alreadyRetried()) cleanRetryMark();
        return;
      }

      // 线上版本与本版不一致 —— 本版这份 HTML 是旧的，跳转一次拿新的。
      // 但如果已经为重试跳转过一次还是旧的，说明跳转救不了
      //（中间层强缓存 / HTML 根本没更新），直接放弃，避免无限刷新把 web-view 卡死。
      if (alreadyRetried()) {
        console.warn('[versionCheck] 已重试过仍为旧版本，放弃本次（可能被中间层强缓存）');
        return;
      }

      console.log('[versionCheck] 检测到新版本，从', HTML_VERSION, '刷新到', online);
      reloadWithRetryMark();
    } catch (e) {
      // 网络异常 / 跨域 / 隐私模式，一律忽略
    }
  }, DELAY_MS);
}
